import moment from 'moment';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import Button from 'material-ui/Button';
import CardMedia from 'material-ui/Card/CardMedia';

import BreadcrumbNav from '../nav/BreadcrumbNav';
import * as Authorization from '../../lib/authorization';
import * as AppState from '../../state/reducer/app/appState';
import * as AnalyticsState from '../../state/reducer/analytics/analytics';

import Console from '../../lib/debug';
import GridCard from '../common/component/GridCard';

const crumbs = [
    {
        displayString: 'Analytics',
        link: `/analytics`,
        editable: false
    }
];

const MAX_USER_ID_FOR_CLASSIC_REPORTS = 2550000;
const USER_IDS_CAN_VIEW_CLASSIC_REPORTS = [2606410];

export class AnalyticsCardsViewClass extends Component {
    constructor(props) {
        super(props);
        AnalyticsState.loadMeta();
    }

    getCards = () => {
        const { metaTree, loggedInUser, headCoach } = this.props;

        if (Object.keys(metaTree.categories).length === 0) {
            return null;
        }

        if (Object.keys(metaTree.instances).length === 0) {
            return null;
        }

        const cards = [];
        Object.keys(metaTree.categories).forEach(categoryKey => {
            const category = metaTree.categories[categoryKey];
            cards.push(this.getCard(category));
        });

        if (loggedInUser.mpEnabled && headCoach) {
            cards.push(this.getMarketplaceAnalyticsCard());
        }

        return <div className="cards">{cards}</div>;
    };

    getCard = category => {
        const cardActionButtons = this.getCardActionButtons(category);
        if (cardActionButtons.length === 0) {
            return;
        }

        const cardMedia = this.getCardMedia(category);
        const cardIconClass = this.getCardIconClass(category);
        return (
            <GridCard
                category={category}
                cardMedia={cardMedia}
                cardIconClass={cardIconClass}
                cardActionButtons={cardActionButtons}
            />
        );
    };

    getMarketplaceAnalyticsCard = () => {
        const { metaTree, loggedInUser } = this.props;
        const instance = metaTree.instances['marketplace-org-summary'];
        const trialsInstance = metaTree.instances['marketplace-trials'];
        const attributionInstance =
            metaTree.instances['marketplace-attributions'];
        const isAuthorized = !loggedInUser.isBasic;

        let cardActionButtons = null;
        if (!isAuthorized) {
            cardActionButtons = [this.getUnauthorizedButton(instance)];
        } else {
            const path = '/analytics/' + instance.key;
            const previousMonth = new moment()
                .subtract(1, 'months')
                .startOf('month');
            const firstDayOfMonth = previousMonth.format('YYYY-MM-DD');
            const previousMonthPath = `${path}?start_date=${firstDayOfMonth}`;
            cardActionButtons = [
                <Button
                    className="button"
                    key={instance.key}
                    href={previousMonthPath}
                >
                    {previousMonth.format('MMMM')} {previousMonth.year()}
                </Button>,
                <Button className="button" key={instance.key} href={path}>
                    Month To Date
                </Button>
            ];

            if (trialsInstance) {
                const trialsPath = '/analytics/' + trialsInstance.key;
                cardActionButtons.push(
                    <Button
                        className="button"
                        key={trialsInstance.key}
                        href={trialsPath}
                    >
                        Free Trials
                    </Button>
                );
            }

            if (attributionInstance) {
                const trialsPath = '/my-store/analytics/attributions/';
                cardActionButtons.push(
                    <Button
                        className="button"
                        key="marketplace-attributions"
                        href={trialsPath}
                    >
                        Attributions
                    </Button>
                );
            }
        }

        return (
            <GridCard
                category={{
                    key: 'marketplace',
                    title: 'Marketplace',
                    description:
                        'Evaluate how your Marketplace teams and programs are performing.'
                }}
                cardMedia={
                    <CardMedia className="card-banner empty">&nbsp;</CardMedia>
                }
                cardIconClass="mdi mdi-tag"
                cardActionButtons={cardActionButtons}
            />
        );
    };

    getCardActionButtons = category => {
        const { metaTree } = this.props;

        const cardActionButtons = [];
        category.instances.forEach(instanceKey => {
            const instance = metaTree.instances[instanceKey];
            if (typeof instance === 'undefined') {
                return;
            }

            if (instance.published === false) {
                return;
            }

            cardActionButtons.push(
                this.getCardActionButtonForInstance(instance)
            );
        });
        return cardActionButtons;
    };

    handleNavigation = path => {
        const { loggedInUser } = this.props;
        const isAuthorized = !loggedInUser.isBasic;

        if (!isAuthorized) {
            return AppState.showUnauthorizedDialog();
        }

        AnalyticsState.navigate(path);
    };

    getCardActionButtonForInstance = instance => {
        const { loggedInUser } = this.props;
        const isAuthorized = !loggedInUser.isBasic;

        if (isAuthorized) {
            return this.getAuthorizedButton(instance);
        }

        return this.getUnauthorizedButton(instance);
    };

    getAuthorizedButton = instance => {
        const path = '/analytics/' + instance.key;
        return (
            <Button className="button" key={instance.key} href={path}>
                {instance.title_short}
            </Button>
        );
    };

    getUnauthorizedButton = instance => {
        const onClick = () => {
            AppState.showUnauthorizedDialog();
        };

        return (
            <Button className="button" key={instance.key} onClick={onClick}>
                Launch
            </Button>
        );
    };

    getCardMedia = category => {
        let cardMedia = (
            <CardMedia className="card-banner empty">&nbsp;</CardMedia>
        );
        if (
            typeof category.message !== 'undefined' &&
            category.message.length > 0
        ) {
            cardMedia = (
                <CardMedia className="card-banner">
                    {category.message}
                </CardMedia>
            );
        }
        return cardMedia;
    };

    getCardIconClass = category => {
        let cardIconClass = '';
        switch (category.key) {
            case 'readiness':
                cardIconClass = 'mdi mdi-human';
                break;
            case 'performance':
                cardIconClass = 'mdi mdi-chart-areaspline';
                break;
            case 'lift-history':
                cardIconClass = 'mdi mdi-dumbbell';
                break;
            case 'training-summary':
                cardIconClass = 'mdi mdi-chart-bar';
                break;
            case 'compliance':
                cardIconClass = 'mdi mdi-margin';
                break;
            case 'progress':
                cardIconClass = 'mdi mdi-trending-up';
                break;
        }
        return cardIconClass;
    };

    handleRedirect = (authorizationObject, newTab = false) => {
        const isAuthorized = authorizationObject.isAllowed();
        if (!isAuthorized) {
            return AppState.showUnauthorizedDialog();
        }

        const url = authorizationObject.path;
        if (newTab) {
            window.open(url);
        } else {
            window.location = url;
        }
    };

    render() {
        Console.render('AnalyticsCardsView');
        const userId = this.props.loggedInUser.id;
        const isClassicReportsEnabled =
            userId < MAX_USER_ID_FOR_CLASSIC_REPORTS ||
            USER_IDS_CAN_VIEW_CLASSIC_REPORTS.includes(userId);

        const cards = this.getCards();
        return (
            <div className="cards-view has-filter-bar">
                <div className="jumbotron jumbotron-analytics">
                    <div>
                        <h1>Welcome to TrainHeroic Analytics</h1>
                        <p>
                            Understand your athletes and teams better than ever
                            with TrainHeroic Analytics.
                        </p>
                    </div>
                    {isClassicReportsEnabled && (
                        <div>
                            <Button
                                className="outlined-button"
                                variant="outlined"
                                aria-label="To Classic Reports"
                                onClick={() =>
                                    this.handleRedirect(
                                        Authorization.navigateToReports
                                    )
                                }
                            >
                                <span>To Classic Reports</span>
                            </Button>
                        </div>
                    )}
                </div>

                <BreadcrumbNav crumbs={crumbs} />
                {cards}
            </div>
        );
    }
}

export default connect((state, props) => {
    return {
        metaTree: state.analytics.metaTree,
        loggedInUser: state.app.loggedInUser,
        role: state.app.role,
        headCoach: state.app.headCoach
    };
})(AnalyticsCardsViewClass);



// WEBPACK FOOTER //
// ./src/view/analytics/AnalyticsCardsView.jsx